import entityReducer from 'syft-acp-core/reducers/generators/entityReducer'
import * as entityTypes from 'syft-acp-core/reducers/generators/entities'
import * as types from 'syft-acp-core/actions/action-types'
import {
  JOB_STOP_OFFERS_UPDATE_BY_SHIFT,
  JOB_FETCH_LTA_EXTENSIONS_SUCCEEDED,
  JOB_EXTEND_LTA_SUCCEEDED,
  JOB_CANCEL_LTA_EXTENSION_SUCCEEDED,
  JOB_EXTEND_BOOKING_BEGIN,
  JOB_EXTEND_BOOKING_FAILED,
  JOB_EXTEND_BOOKING_SUCCEEDED,
  JOB_EXTEND_AGENCY_BOOKING_BEGIN,
  JOB_EXTEND_AGENCY_BOOKING_FAILED,
  JOB_EXTEND_AGENCY_BOOKING_SUCCEEDED,
} from './action-types'

import { JobEntity, JobsAdditionalState, JobsState, AdminListingFetchSucceededAction } from './types'

const workerKey = (jobID: number | string, workerID: number | string) => `${jobID}_worker_${workerID}`
const agencyKey = (jobID: number | string, agencyWorkerID: number | string) =>
  `${jobID}_agency_${agencyWorkerID}`

const setInFlight = (state: JobsState, key: string): JobsState => ({
  ...state,
  extendBookingInFlight: { ...state.extendBookingInFlight, [key]: true },
})

const clearInFlight = (state: JobsState, key: string): JobsState => {
  const { [key]: _, ...rest } = state.extendBookingInFlight
  return { ...state, extendBookingInFlight: rest }
}

export default entityReducer<JobEntity, JobsAdditionalState>(
  'jobs',
  {
    [entityTypes.ENTITIES_FETCH_BEGIN]: types.FETCH_ADMIN_JOB_BEGIN,
    [types.FETCH_ADMIN_JOB_SUCCEEDED]: (state: JobsState, action: AdminListingFetchSucceededAction) => {
      const job = action.payload
      const existingJob = state.entityMap[job.id] || {}

      return {
        ...state,
        lastMessage: '',
        lastStatus: null,
        lastBody: null,
        isLoadingData: false,
        entitySets: {
          ...state.entitySets,
        },
        entityMap: {
          ...state.entityMap,
          [job.id]: {
            ...job,
            ltaExtensions: existingJob.ltaExtensions,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [job.id]: {
            ...job,
            ltaExtensions: existingJob.ltaExtensions,
          },
        },
      }
    },
    [entityTypes.ENTITIES_FETCH_FAILED]: [types.FETCH_ADMIN_JOB_FAILED, types.ADMIN_LISTING_FETCH_FAILED],

    [entityTypes.ENTITY_MODIFY_BEGIN]: types.JOB_UPDATE_BEGIN,
    // This is a hack because after update a job for the public endpoint doesn't return roles. So we call get endpoint.
    // Please uncomment this after new endpoint for job will be avaliable. https://syftapp.atlassian.net/browse/PTE-20205
    // And remove yield put(fetchAdminJob(action.request.id)) from jobs/saga.ts
    // [entityTypes.ENTITY_MODIFY_SUCCEEDED]: types.JOB_UPDATE_SUCCEEDED,
    [entityTypes.ENTITY_MODIFY_FAILED]: types.JOB_UPDATE_FAILED,
    [types.SHIFT_DELETE_SUCCEEDED]: (state: JobsState, action: any) => {
      const shifts = state.entityMap[action?.request?.jobID]?.shifts?.filter(shift => {
        return shift.id !== action.request.shiftID
      })
      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [action.request.jobID]: {
            ...state.entityMap[action.request.jobID],
            shifts: shifts ?? state.entityMap[action.request.jobID].shifts,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [action.request.jobID]: {
            ...state.entityMap[action.request.jobID],
            shifts: shifts ?? state.entityMap[action.request.jobID].shifts,
          },
        },
      }
    },

    [JOB_STOP_OFFERS_UPDATE_BY_SHIFT]: (state: JobsState, action: any) => {
      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [action.id]: {
            ...state.entityMap[action.id],
            stop_offers_for_worker_type: action.payload,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [action.id]: {
            ...state.entityMap[action.id],
            stop_offers_for_worker_type: action.shiftOfferForWorkerType,
          },
        },
      }
    },

    [entityTypes.ENTITY_DELETE_BEGIN]: types.RATING_DELETE_BEGIN,
    [entityTypes.ENTITY_DELETE_SUCCEEDED]: types.RATING_DELETE_SUCCEEDED,
    [entityTypes.ENTITY_DELETE_FAILED]: types.RATING_DELETE_FAILED,

    [JOB_FETCH_LTA_EXTENSIONS_SUCCEEDED]: (state: JobsState, action: any) => {
      const jobID = action.request.jobID
      const ltaExtensionsData = action.payload.lta_extension_job
      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [jobID]: {
            ...state.entityMap[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [jobID]: {
            ...state.entityDetail[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
      }
    },

    [JOB_EXTEND_LTA_SUCCEEDED]: (state: JobsState, action: any) => {
      const jobID = action.request.jobID
      const ltaExtensionsData = action.payload.lta_extension_job
      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [jobID]: {
            ...state.entityMap[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [jobID]: {
            ...state.entityDetail[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
      }
    },

    [JOB_CANCEL_LTA_EXTENSION_SUCCEEDED]: (state: JobsState, action: any) => {
      const jobID = action.request.jobID
      const ltaExtensionsData = action.payload.lta_extension_job
      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [jobID]: {
            ...state.entityMap[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
        entityDetail: {
          ...state.entityDetail,
          [jobID]: {
            ...state.entityDetail[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
      }
    },

    [JOB_EXTEND_BOOKING_BEGIN]: (state: JobsState, action: any) =>
      setInFlight(state, workerKey(action.jobID, action.workerID)),

    [JOB_EXTEND_BOOKING_FAILED]: (state: JobsState, action: any) =>
      clearInFlight(state, workerKey(action.request.jobID, action.request.workerID)),

    [JOB_EXTEND_BOOKING_SUCCEEDED]: (state: JobsState, action: any) => {
      const jobID = action.request.jobID
      const ltaExtensionsData = action.payload.lta_extension_job
      const cleared = clearInFlight(state, workerKey(jobID, action.request.workerID))
      return {
        ...cleared,
        entityMap: {
          ...cleared.entityMap,
          [jobID]: {
            ...cleared.entityMap[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
        entityDetail: {
          ...cleared.entityDetail,
          [jobID]: {
            ...cleared.entityDetail[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
      }
    },

    [JOB_EXTEND_AGENCY_BOOKING_BEGIN]: (state: JobsState, action: any) =>
      setInFlight(state, agencyKey(action.jobID, action.agencyWorkerID)),

    [JOB_EXTEND_AGENCY_BOOKING_FAILED]: (state: JobsState, action: any) =>
      clearInFlight(state, agencyKey(action.request.jobID, action.request.agencyWorkerID)),

    [JOB_EXTEND_AGENCY_BOOKING_SUCCEEDED]: (state: JobsState, action: any) => {
      const jobID = action.request.jobID
      const ltaExtensionsData = action.payload.lta_extension_job
      const cleared = clearInFlight(state, agencyKey(jobID, action.request.agencyWorkerID))
      return {
        ...cleared,
        entityMap: {
          ...cleared.entityMap,
          [jobID]: {
            ...cleared.entityMap[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
        entityDetail: {
          ...cleared.entityDetail,
          [jobID]: {
            ...cleared.entityDetail[jobID],
            ltaExtensions: ltaExtensionsData,
          },
        },
      }
    },
  },
  {
    initialState: { extendBookingInFlight: {} },
  },
)
